/* eslint-disable react/jsx-no-bind, react/jsx-one-expression-per-line -- day-cell markup: the renderers are per-cell closures and the label text interleaves with elements */
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import { alpha, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { DateRangeCalendar } from '@mui/x-date-pickers-pro/DateRangeCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { PatchedAdapterDateFns } from 'providers/PatchedAdapterDateFns'
import { RECURRENCE_TO_HEATMAP_VALUE } from 'settings/constants/workOrder'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'
import { deserializeDate, formatDateToBEFormatDateFns, formatDateToFEFormatDateFns } from 'utils/date'

import CalendarDay from './CalendarDay/CalendarDay'
import DateRangePresets from './DateRangePresets'
import StatusLegend from './StatusLegend'
import { useDateRangePresets } from './useDateRangePresets'
import { useVacationHolds } from './useVacationHolds'
import { useWorkOrderData } from './useWorkOrderData'

const loadingOverlaySx = theme => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: Z_INDEX.MAP_OVERLAY(theme),
  bgcolor: alpha(theme.palette.common.white, 0.7),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const EventsCalendar = ({
  locationId,
  isActive,
  apiHeatmap,
  byDate,
  vacationHoldData,
  paymentsByDate,
  isCalendarLoading,
  expandWindowIfNeeded,
  ensureDateInWindow,
}) => {
  const theme = useTheme()
  const { setValue } = useFormContext()
  const serializedServiceDate = useSelector(state => get(state, 'CustomerDetails.routing.serializedServiceDate'))
  const serviceDate = useMemo(() => (serializedServiceDate ? deserializeDate(serializedServiceDate) : new Date()), [serializedServiceDate])

  const isXl = useMediaQuery(theme.breakpoints.up('xl'))
  const isLg = useMediaQuery(theme.breakpoints.up('lg'))
  const fontSize = useMemo(() => (isXl ? 14 : 11), [isXl])
  const calendarsCount = useMemo(() => (isLg ? 2 : 1), [isLg])

  const calendarRef = useRef(null)
  const [calendarHeight, setCalendarHeight] = useState(264)

  const [visibleMonth, setVisibleMonth] = useState(() => serviceDate)

  const {
    selectedRange,
    activePreset,
    applyPreset,
    handleDateChange: handleDateChangeFromHook,
    handleDayClick: handleDayClickFromHook,
  } = useDateRangePresets(serviceDate)

  const handleDateChange = useCallback(
    newDateRange => {
      handleDateChangeFromHook(newDateRange)
    },
    [handleDateChangeFromHook]
  )

  const handleMonthChange = useCallback(
    newMonth => {
      setVisibleMonth(newMonth)
      expandWindowIfNeeded(newMonth)
    },
    [expandWindowIfNeeded]
  )

  useEffect(() => {
    setVisibleMonth(serviceDate)
  }, [serviceDate])

  useEffect(() => {
    if (isActive) {
      ensureDateInWindow(visibleMonth)
    }
  }, [isActive, visibleMonth, ensureDateInWindow])

  useEffect(() => {
    setValue('selectedDateRange', selectedRange)
  }, [selectedRange, setValue])

  const { isDateInVacationHold } = useVacationHolds(vacationHoldData, locationId)

  const {
    workOrderCountByDate,
    exceptionsByDate,
    invoicesByDate,
    disposalTicketsByDate,
    feesByDate,
    notesByDate,
    routesByDate,
    workOrderStatusesByDate,
    rowsByDate,
  } = useWorkOrderData(isDateInVacationHold, byDate)

  useEffect(() => {
    const element = calendarRef.current
    if (!element) return undefined

    const calculateHeight = () => {
      const monthContainer = element.querySelector('.MuiDateRangeCalendar-monthContainer')
      if (monthContainer) {
        const width = monthContainer.offsetWidth
        const cellWidth = width / 7
        const calculatedHeight = cellWidth * 6
        setCalendarHeight(calculatedHeight)
      }
    }

    const observer = new ResizeObserver(calculateHeight)
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  // Handler functions
  const getHeatmapValueForDate = useCallback(
    date => {
      if (!date) return RECURRENCE_TO_HEATMAP_VALUE.EMPTY

      const dateObj = date instanceof Date ? date : new Date(date)

      if (Number.isNaN(dateObj.getTime())) {
        return RECURRENCE_TO_HEATMAP_VALUE.EMPTY
      }

      const dateStr = formatDateToBEFormatDateFns(dateObj)
      const dayData = apiHeatmap[dateStr]

      if (!dayData) return RECURRENCE_TO_HEATMAP_VALUE.EMPTY

      const recurringCount = get(dayData, 'recurringCount', 0)
      const requestCount = get(dayData, 'requestCount', 0)

      if (recurringCount > 0 && requestCount === 0) {
        return RECURRENCE_TO_HEATMAP_VALUE.RECURRING
      }
      if (recurringCount === 0 && requestCount > 0) {
        return RECURRENCE_TO_HEATMAP_VALUE.ON_REQUEST
      }
      if (recurringCount > 0 && requestCount > 0) {
        return RECURRENCE_TO_HEATMAP_VALUE.MIXED
      }

      return RECURRENCE_TO_HEATMAP_VALUE.EMPTY
    },
    [apiHeatmap]
  )

  const renderDay = useCallback(
    dayProps => {
      if (!dayProps) return null

      const dayObj = dayProps.day

      if (!dayObj || Number.isNaN(dayObj.getTime())) {
        return null
      }

      if (dayProps.outsideCurrentMonth) {
        return <Box sx={{ width: '100%', aspectRatio: '1 / 1' }} />
      }

      const dateStr = formatDateToBEFormatDateFns(dayObj)
      const dayData = apiHeatmap[dateStr]
      const workOrderCount = workOrderCountByDate[dateStr] || 0
      const hasWorkOrdersForDay = workOrderCount > 0

      const heatmapValue = hasWorkOrdersForDay ? getHeatmapValueForDate(dayObj) : RECURRENCE_TO_HEATMAP_VALUE.EMPTY

      const exceptionCount = exceptionsByDate[dateStr] || 0
      const invoiceCount = invoicesByDate[dateStr] || 0
      const disposalTicketCount = disposalTicketsByDate[dateStr] || 0
      const feeCount = feesByDate[dateStr] || 0
      const noteCount = notesByDate[dateStr] || 0
      const paymentCount = paymentsByDate[dateStr] || 0
      const routeData = routesByDate[dateStr]
      const workOrderStatuses = workOrderStatusesByDate[dateStr] || []
      const facetRow = rowsByDate[dateStr]

      const handleDayClick = () => {
        handleDayClickFromHook(dayObj)
      }

      return (
        <CalendarDay
          dateStr={dateStr}
          day={dayProps}
          dayData={dayData}
          disposalTicketCount={disposalTicketCount}
          exceptionCount={exceptionCount}
          feeCount={feeCount}
          heatmapValue={heatmapValue}
          invoiceCount={invoiceCount}
          noteCount={noteCount}
          paymentCount={paymentCount}
          pickersDayProps={dayProps}
          recurringServiceAction={facetRow?.recurringServiceAction}
          routeData={routeData}
          selectedRange={selectedRange}
          workOrderCount={workOrderCount}
          workOrderStatuses={workOrderStatuses}
          onDayClick={handleDayClick}
          onRequestServiceAction={facetRow?.onRequestServiceAction}
        />
      )
    },
    [
      apiHeatmap,
      workOrderCountByDate,
      rowsByDate,
      exceptionsByDate,
      invoicesByDate,
      disposalTicketsByDate,
      feesByDate,
      notesByDate,
      paymentsByDate,
      routesByDate,
      workOrderStatusesByDate,
      selectedRange,
      getHeatmapValueForDate,
      handleDayClickFromHook,
    ]
  )

  const selectedRangeText = useMemo(() => {
    if (selectedRange && selectedRange[0] && selectedRange[1]) {
      const startDateFormatted = formatDateToFEFormatDateFns(selectedRange[0])
      const endDateFormatted = formatDateToFEFormatDateFns(selectedRange[1])
      return `${startDateFormatted} - ${endDateFormatted}`
    }
    if (selectedRange && selectedRange[0]) {
      return formatDateToFEFormatDateFns(selectedRange[0])
    }
    return T.ALL_WORK_ORDERS_NO_DATE_FILTER
  }, [selectedRange])

  return (
    <Box
      alignItems="flex-start"
      display="flex"
      gap={2}
      position="relative"
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      width="100%"
    >
      {/* eslint-disable-next-line no-restricted-syntax -- 10px aligns this rail with the calendar header, which is not on the 8px grid */}
      <Box alignItems="center" display="flex" flexDirection="column" gap={1} sx={{ mt: '10px', width: '270px' }}>
        <StatusLegend />
        <Box fullWidth alignItems="center" display="flex" justifyContent="space-between" mb={1} px={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ textTransform: 'uppercase' }} variant="body2">
              {T.DATE_FILTER}:
            </Typography>
            <Typography sx={{ fontWeight: '300' }} variant="h6">
              {selectedRangeText}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ width: '50%' }} />
        <DateRangePresets activePreset={activePreset} onPresetClick={applyPreset} />
      </Box>
      <Box flex={1} position="relative">
        {isCalendarLoading && (
          <Box sx={loadingOverlaySx}>
            <CircularProgress />
          </Box>
        )}
        <LocalizationProvider dateAdapter={PatchedAdapterDateFns}>
          <Box ref={calendarRef}>
            <DateRangeCalendar
              calendars={calendarsCount}
              slots={{
                day: renderDay,
              }}
              sx={{
                width: '100%',
                '& .MuiPickersDay-root': {
                  // eslint-disable-next-line no-restricted-syntax -- fontSize is breakpoint-derived per instance (isXl ? 14 : 11); a themeV5 styleOverrides entry is global and cannot read it
                  fontSize,
                },
                '& .MuiDayCalendar-header': {
                  '& .MuiTypography-root': {
                    // eslint-disable-next-line no-restricted-syntax -- same breakpoint-derived fontSize
                    fontSize,
                    fontWeight: 700,
                  },
                },
                '& .MuiPickersCalendarHeader-label': {
                  // eslint-disable-next-line no-restricted-syntax -- same breakpoint-derived fontSize
                  fontSize,
                  fontWeight: 700,
                },
                '& .MuiDateRangeCalendar-monthContainer': {
                  width: calendarsCount === 1 ? '100%' : '50%',
                  padding: 1,
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  width: 'calc(100%/7)',
                },
                '& .MuiPickersSlideTransition-root': {
                  minHeight: `${calendarHeight}px`,
                },
                '& .MuiDayCalendar-slideTransition': {
                  minHeight: `${calendarHeight}px`,
                },
              }}
              value={selectedRange}
              onChange={handleDateChange}
              onMonthChange={handleMonthChange}
            />
          </Box>
        </LocalizationProvider>
      </Box>
    </Box>
  )
}

EventsCalendar.propTypes = {
  locationId: PropTypes.string,
  isActive: PropTypes.bool,
  apiHeatmap: PropTypes.object,
  byDate: PropTypes.array,
  vacationHoldData: PropTypes.object,
  paymentsByDate: PropTypes.object,
  isCalendarLoading: PropTypes.bool,
  expandWindowIfNeeded: PropTypes.func,
  ensureDateInWindow: PropTypes.func,
}

export default EventsCalendar
